"use client"
import type React from "react"
import Image from "next/image"
import Button from "../ui/Button"
import { useRouter } from "next/navigation"

interface Step {
  id: number
  title: string
  description: string
  image: string
  width?: number
  height?: number
  class?: string
  wheelChair?: boolean
}

const LesAvantages = ({ methode = false }: { methode?: boolean }) => {
  const router = useRouter()
  const steps: Step[] = [
    {
      id: 1,
      title: "Orientation &\nTest de niveau",
      description: "Parcours de formation personnalisé",
      image: "/assets/icons/orientation-level-test.svg",
      width: 198,
      height: 178,
      class: "xl:w-full lg:w-[160px] w-full lg:mb-2.5 mt-3"
    },
    {
      id: 2,
      title: "Inscription",
      description: "",
      image: "/assets/icons/inscription.svg",
      width: 119,
      height: 105,
      class: "xl:w-full lg:w-[160px] w-full lg:mt-15.5 lg:mb-6.5",
    },
    {
      id: 3,
      title: "Démarrage\nde la Formation",
      description: "Accessibilité réduite disponible",
      image: "/assets/icons/training-start.svg",
      width: 212.65,
      height: 190,
      wheelChair: true,
      class: "xl:w-full lg:w-[160px] w-full lg:mt-16.75",
    },
    {
      id: 4,
      title: "Évaluation",
      description: "",
      image: "/assets/icons/evaluation.svg",
      width: 164.72,
      height: 154.96,
      class: "xl:w-full lg:w-[160px] w-full lg:mt-27 lg:-mb-2",
    },
    {
      id: 5,
      title: "Coaching",
      description: "",
      image: "/assets/icons/coaching.svg",
      width: 192,
      height: 173,
      class: "xl:w-full lg:w-[160px] w-full lg:mt-10",
    },
  ]

  const services = [
    "Un accompagnement adapté à tous",
    "Des formateurs et organismes rigoureusement sélectionnés",
    "Présentiel, distanciel ou mixte",
    "Une mise en relation qui a du sens, à chaque étape",
  ]

  return (
    <section className="w-full bg-[#FFE4B4]/30 pt-10.5 pb-17.5 px-4">
      <div className="max-w-346.25 mx-auto relative">
        {/* Header Section */}
        <div className="text-center mb-8">
          <h2 className="text-[30px] md:text-[40px] font-light font-red-hat leading-11.5 md:leading-15.75 text-[#1B365E] mb-8 sm:mb-10.75">
            {!methode ? "Nous avons une solution adaptée à vos attentes" : "Une méthode de formation unique"}
          </h2>

          <p className="text-[16px] sm:text-[20px] font-bold sm:font-medium text-[#1D1D1D] max-w-189.25 mx-auto leading-6.5 sm:leading-7.5">
            Nous accoumpagnons vers la réussite, en s&apos;appuyant sur des formateurs et organismes rigoureusement sélectionnés, pour des mises en relation
            qui ont du sens.
          </p>
        </div>

        {!methode &&
        <div className="xl:absolute right-0 top-22.5 flex justify-center mb-8">
          <div className="inline-flex items-center gap-2 bg-white rounded-lg p-2">
            <Image className="w-12 shrink-0" src="/assets/clicking.svg" alt="Clicking" title="Clicking" width={48} height={48} />
            <div className="space-y-1">
              <div className="flex gap-2">
                {[...Array(5)].map((_, i) => (
                  <Image key={i} className="w-4 shrink-0" src="/assets/icons/star.png" alt="Star" title="Star" width={16} height={16} />
                ))}
              </div>
              <p className="text-xs text-[#1B515E] leading-5 tracking-[2%] ">
                Évaluation moyenne
                <br />
                de nos formations
              </p>
            </div>
          </div>
        </div>
        }

        {methode &&
        <div className="md:w-126.75 h-[263.46px] relative mx-auto mb-[49.54px]">
          <Image src="/assets/video-placeholder.png" alt="Vider Placeholder" title="Video Placeholder" objectFit="cover" fill />
          <Image className="absolute bottom-[49.97px] left-1/2 transition-translate -translate-x-1/2" src="/assets/logo-blue.svg" alt="Logo" title="Spot Formation" width={168.49} height={11.49} />
          <Image className="absolute top-1/2 left-1/2 transition-translate -translate-x-1/2 -translate-y-1/2" src="/assets/icons/play.svg" alt="Play" title="Play" width={40} height={40} />
        </div>
        }

        {/* Services Dots */}
        <div className="flex lg:flex-nowrap flex-wrap md:justify-center gap-8 sm:gap-15 mb-8 sm:mb-16 w-full md:max-w-full max-w-64.5 mx-auto">
          {services.map((service, index) => (
            <div key={index} className="flex items-center gap-3.25 mt-8">
              <div className="w-5.25 h-5.25 rounded-full bg-[#40BFDE] shrink-0"></div>
              <span className={` text-[#1B365E] font-bold leading-6.5 text-[16px]`}>{service}</span>
            </div>
          ))}
        </div>

        {/* Timeline Section */}
        <div className="relative lg:pt-6 lg:pb-12">
          {/* Dotted path background */}
          <Image className="absolute inset-0 w-full h-full -top-19 xl:-left-6.5 lg:block hidden pointer-events-none" src="/assets/curly-line-horizontal.svg" alt="Dotted Line Horizontal" title="Dotted Line Horizontal" width={1297} height={291} />
          <Image className="absolute inset-0 w-full h-full top-1 lg:hidden block pointer-events-none" src="/assets/curly-line-vertical.svg" alt="Dotted Line Vertical" title="Dotted Line Vertical" width={507.52} height={1205.09} />

          {/* Steps Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-5 lg:gap-0 gap-4 relative z-10">
            {steps.map((step) => (
              <div key={step.id} className="flex flex-col items-center">
                {/* Image Container */}
                <div className={`flex justify-center ${step.class || ""}`}>
                  <Image src={step.image || "/placeholder.svg"} alt={step.title} title={step.title} width={step.width ?? 198} height={step.height ?? 178} />
                </div>

                {/* Title */}
                <h3 className="text-center font-light font-red-hat text-[#1B365E] text-[30px] lg:text-[24px] xl:text-[30px] leading-[44.5px] lg:leading-9.5 xl:leading-[44.5px] tracking-[0.05em] mb-2 whitespace-pre-line">
                  {step.title}
                </h3>

                <div className="flex items-center gap-2.25">
                  {step.wheelChair && <Image src="/assets/icons/wheel-chair.svg" alt="Wheel Chair" title="Wheel Chair" width={21} height={24} />}
                  {step.description && <p className="text-center text-xs text-[#1B365E] leading-5">{step.description}</p>}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* CTA Button */}
        <div className="flex justify-center lg:mb-11.75 mb-12 lg:mt-0 mt-8">
          <Button variant="gradient" size="unset" onClick={() => router.push('/formations')} className="w-full sm:w-fit rounded-lg leading-6.5 text-[15px] py-4 px-10 font-bold">
            Découvrir toutes nos formations
          </Button>
        </div>

        {/* Footer Info */}
        <div className="flex lg:flex-row flex-col gap-12 lg:justify-center items-center">
          {/* <div className={`lg:absolute right-0 ${!methode ? "-bottom-1" : "bottom-13.75"} flex items-center md:gap-3 gap-2.75 text-[14px] leading-5 font-normal text-[#1B365E]`}>
            <Image className="md:w-6 w-[22.49px] md:h-[34.43px] h-[32.27px] shrink-0" src="/assets/bulb.svg" alt="Bulb" title="Bulb" width={24} height={34.43} />
            <span>
              Prise en charge OPCO...
              <br />
              Optimisez le <span className="underline underline-offset-2 cursor-pointer">financement</span> de votre formation
            </span>
          </div> */}
          {!methode &&
            <Image className="w-53.75" src="/assets/logo-blue.svg" alt="Spot Formations" title="Spot Formations" width={258} height={17.49} />
          }
        </div>
      </div>
    </section>
  )
}

export default LesAvantages